<template>
  <Card
    class="data-table-card"
    :data-id="dataId || `data-table-${title}`"
    :data-loading="loading ? '1' : '0'"
    :no-card="noCard"
    no-body
    :no-footer="!hasFooter"
    :overflow-hidden="false"
  >
    <!-- hidden test inputs -->
    <input :value="length ? length : 0" name="data-table-count" type="hidden" />
    <input
      :value="rowCount ? rowCount : 0"
      name="data-row-count"
      type="hidden"
    />
    <!-- header -->
    <template #header>
      <DataTableHeader
        :description="description"
        :disable-new="disableNew"
        :enable-delete-all="enableDeleteAll"
        :icon="icon"
        :length="noCount ? undefined : length"
        :loading="loading"
        :no-card="noCard"
        :has-filter="hasFilter"
        :search-filter="searchFilter"
        :title="title"
        :active-tag="activeTag"
        :tags="tags"
        :enable-date-picker="enableDatePicker"
        :has-unread-only-toggle="hasUnreadOnlyToggle"
        :has-unseen-only-toggle="hasUnseenOnlyToggle"
        :page-size="pageSize"
        :filter-label="filterLabel"
        @pagesize="onPageSize"
        @date-range="onDateRange"
        @new="onNew"
        @tags-changed="onTagsChanged"
        @delete-all="onDeleteAll"
        @filter-change="onFilterChange"
        @refresh="onRefresh"
        @unread-only="onUnreadOnly"
        @unseen-only="onUnseenOnly"
      >
        <template #title>
          <slot name="title">{{ title }}</slot>
        </template>
        <template #filter>
          <slot name="filter" />
        </template>
        <template v-if="hasMore" #more>
          <slot name="more" />
        </template>
      </DataTableHeader>
    </template>
    <!-- body -->
    <section
      class="relative overflow-x-auto"
      data-el="data-table-body"
      :data-table-length="length ? length.toString(10) : '0'"
    >
      <SpinnerFormMask :loading="loading" />
      <!-- table -->
      <Table :class="loading ? 'invisible' : ''" flush>
        <!-- thead -->
        <!-- eslint-disable-next-line -->
        <TableHeader
          v-if="items && items.length > 0"
          :has-slot="hasSlots"
          :fields="_fields"
          :has-actions="hasActions"
          :has-favourites="hasFavourites"
          :sort="sort"
          @sort="onSortChange"
        />
        <!-- results  -->
        <DataTableResults
          v-if="items && items.length > 0"
          :disable-delete="disableDelete"
          :disable-edit="disableEdit"
          :enable-redrive="enableRedrive"
          :fields="_fields"
          :has-actions="hasActions"
          :items="items"
          @redrive="onRedrive"
          @delete="onDelete"
          @edit="onEdit"
          @toggle="onToggle"
        >
          <template v-if="!!hasSlots" #row="{ item, index }">
            <slot name="row" :item="item" :index="index" />
          </template>
        </DataTableResults>
        <!-- no results -->
        <DataTableNoResults
          v-else
          :message="noResultsMessage"
          :disable-new="disableNew"
          :search-filter="searchFilter"
          :page="page"
          @new="onNew"
        />
      </Table>
    </section>
    <template #footer>
      <footer class="sticky bottom-0 text-center" v-if="hasFooter">
        <Pagination
          class="d-inline-flex page-text-sm mb-0 ml-auto"
          v-if="(totalElements || 0) > 1 || isInfinite"
          v-model="pagerIndex"
          :no-count="noCount"
          :per-page="pageable ? pageable.pageSize : 0"
          :total-rows="totalElements"
          aria-controls="data-table"
          @change="handlePage"
        />
        <span class="text-lighter" v-else>-</span>
      </footer>
    </template>
  </Card>
</template>
<script setup lang="ts" generic="T extends DataTableItem">
import type { PageableObject as Pageable } from '@mailslurp/admin-sdk';
import type { Field } from '../../../lib/services/helpers';
import type { DataTableEditResult } from '../../../lib/services/types';
import DataTableNoResults from '../../../components/table/DataTableNoResults.vue';
import DataTableHeader from '../../../components/table/parts/DataTableHeader.vue';
import TableHeader from '../../../components/table/parts/TableHeader.vue';
import DataTableResults from '../../../components/table/parts/DataTableResults.vue';
import SpinnerFormMask from '../../../components/table/SpinnerFormMask.vue';
import type { DateRange } from '../../../lib/services/TimeService';
import { ref, computed, useSlots } from 'vue';
import type { Sort } from '../../../store/types';
import Pagination from '../../../components/form/Pagination.vue';
import Card from '../../../components/tw/Card.vue';
import Table from '../../../components/table/parts/Table.vue';
import type {
  DataTableItem,
  MsIcon,
  TogglePayload,
} from '../../../lib/services/types';

import { useNuxtApp } from '#imports';
import { getLogger } from '../../../lib/services/getLogger';
const { $captureEvent: captureEvent } = useNuxtApp();
const log = getLogger('DataTable');

const hasFooter = computed(() => {
  return (
    ((props.pageable &&
      props.items &&
      props.items.length > 0 &&
      (props.totalElements ?? 0) > 1) ||
      isInfinite.value) &&
    !noTotalElements.value
  );
});

const isInfinite = computed(() => {
  return props.totalElements === -1;
});

const emit = defineEmits<{
  (e: 'unseenOnly', p: boolean): void;
  (e: 'unreadOnly', p: boolean): void;
  (e: 'new'): void;
  (e: 'refresh'): void;
  (e: 'deleteAll'): void;
  (e: 'tagsChanged', p: string): void;
  (e: 'toggle', p: TogglePayload): void;
  (e: 'filterChange', p: string): void;
  (e: 'edit', p: DataTableEditResult<T>): void | Promise<void>;
  (e: 'sort', p: Sort): void;
  (e: 'page', p: number): void;
  (e: 'redrive', p: T): void;
  (e: 'delete', p: T): void;
  (e: 'dateRange', p: DateRange | undefined): void;
  (e: 'pagesize', p: number): void;
}>();
const onPageSize = (pageSize: number) => {
  emit('pagesize', pageSize);
};
const slots = useSlots();
const hasSlots = computed(() => !!slots.row);

const captureAction = (
  action: string,
  data: { [key: string]: string | number | boolean } = {},
) => {
  captureEvent('data_table_action', { action, dataId: props.dataId, ...data });
};

const onUnseenOnly = (newValue: boolean) => {
  captureAction('unseenOnly');
  emit('unseenOnly', newValue);
};

const onUnreadOnly = (newValue: boolean) => {
  captureAction('unreadOnly');
  emit('unreadOnly', newValue);
};

const onNew = () => {
  captureAction('new');
  emit('new');
};

const onTagsChanged = (payload: string) => {
  emit('tagsChanged', payload);
};

const onToggle = (payload: TogglePayload) => {
  captureAction('toggle');
  emit('toggle', payload);
};

const onRefresh = () => {
  captureAction('refresh');
  emit('refresh');
};

const onFilterChange = (payload: string) => {
  emit('filterChange', payload);
};

const onEdit = (payload: DataTableEditResult<T>) => {
  captureAction('edit');
  emit('edit', payload);
};

const onSortChange = () => {
  const s = props.sort === 'ASC' ? 'DESC' : 'ASC';
  captureAction('sort', { sort: s });
  emit('sort', s);
};

const onRedrive = (item: T) => {
  captureAction('redrive');
  emit('redrive', item);
};

const onDateRange = (dateRange: DateRange) => {
  emit('dateRange', dateRange ?? undefined);
};

const handlePage = (bPagerIndex1Based: number) => {
  const index = bPagerIndex1Based - 1;
  captureAction('paginate', { index });
  emit('page', index);
};

const onDeleteAll = () => {
  log('onDeleteAll');
  if (window.confirm('Are you sure you want to delete every item?' as string)) {
    captureAction('deleteAll');
    log('onDeleteAll accepted');
    emit('deleteAll');
  }
};

const noTotalElements = computed(() => {
  return !props.searchFilter && props.page === 0 && props.items?.length === 0;
});

const props = withDefaults(
  defineProps<{
    noResultsMessage?: string;
    hasUnreadOnlyToggle?: boolean;
    dataId: string;
    noCard?: boolean;
    enableDatePicker?: boolean;
    description?: string;
    icon?: MsIcon;
    hasFilter?: boolean;
    enableRedrive?: boolean;
    fields: Field<T>[];
    items: T[];
    title: string;
    page?: number;
    pageSize?: number;
    pageable?: Pageable | null;
    totalElements?: number | null;
    searchFilter?: string;
    loading: boolean;
    sort?: 'DESC' | 'ASC' | undefined;
    hasUnseenOnlyToggle?: boolean;
    hasFavourites?: boolean;
    tags?: string[];
    activeTag?: string;
    disableEdit?: boolean;
    disableNew?: boolean;
    disableDelete?: boolean;
    enableDeleteAll?: boolean;
    noCount?: boolean;
    hasMore?: boolean;
    filterLabel?: boolean;
  }>(),
  {
    noResultsMessage: '',
    filterLabel: undefined,
    page: undefined,
    pageSize: undefined,
    pageable: undefined,
    hasUnreadOnlyToggle: false,
    dataId: '',
    noCard: false,
    enableDatePicker: false,
    enableRedrive: false,
    searchFilter: '',
    sort: undefined,
    tags: undefined,
    activeTag: '',
    description: undefined,
    icon: undefined,
    totalElements: 0,
    noCount: false,
    hasMore: false,
  },
);

const pagerIndex = ref<number | null>(1);

const hasActions = computed(() => {
  return (
    !props.disableDelete ||
    !props.disableEdit ||
    !props.disableNew ||
    props.enableRedrive
  );
});

const _fields = computed(() => {
  return props.fields ?? [];
});
const rowCount = computed(() => {
  return props.items ? props.items.length : 0;
});
const length = computed(() => {
  if (props.totalElements) {
    return props.totalElements;
  } else {
    return props.items ? props.items.length : 0;
  }
});

const onDelete = (item: T) => {
  if (window.confirm('Are you sure you want to delete this item?' as string)) {
    captureAction('delete');
    emit('delete', item);
  }
};
</script>
